import { Badge } from "@/components/ui/badge"
import { GradientSparkleIcon } from "@/components/extraction/GradientSparkleIcon"
import { cn } from "@/lib/utils"
import {
  categoryAverage,
  categoryAvgTone,
  displayCategoryLabel,
  humanizeCategorySlug,
  zoneTone,
  pendingAiSuggestionCount,
  projectedScore,
  RECOMMENDATION_LABEL,
  TONE_BAR_CLASS,
  TONE_PILL_CLASS,
} from "@/lib/gng-score"
import { useStrategyStore, type GoNoGoAssessment } from "@/stores/strategy-store"
import type { GngCategoryDef } from "@/stores/strategy-settings-store"

interface GoNoGoScoreCardProps {
  assessment: GoNoGoAssessment
  categoryDefs: GngCategoryDef[]
}

export function GoNoGoScoreCard({ assessment, categoryDefs }: GoNoGoScoreCardProps) {
  // Read from the store rather than taking a new prop: the card is only ever
  // rendered inside the store-backed assessment screen.
  const isAiScoring = useStrategyStore((s) => s.isAiScoring)
  const scored = assessment.criteria.filter((c) => c.score != null).length
  const total = assessment.criteria.length
  const score = assessment.overallScore ?? 0
  const rec = assessment.recommendation
  const { go, conditionalGo } = assessment.thresholds
  const tone = zoneTone(score, assessment.thresholds)
  const hasScores = scored > 0

  // While AI suggestions sit unapplied, show what the score would become. Without
  // this the headline reads 0/100 after a successful scoring run, which is what
  // made working AI look broken (finding #4). Suppressed once a decision exists —
  // a decided assessment shouldn't keep nagging.
  // Also suppressed while a scoring run is in flight — the suggestions are
  // still being rewritten, so projecting the previous run's numbers would
  // contradict the suggestion bar, which hides for the same reason.
  // Both halves are needed: an in-session re-score only flips the local flag
  // (the server row is not re-read mid-run), while a run resumed after a
  // remount only has the server status. Same predicate as GoNoGoAssessment.
  const pendingSuggestions = pendingAiSuggestionCount(assessment.criteria)
  const isScoringRun = isAiScoring || assessment.aiScoringStatus === 'scoring'
  const showProjection = pendingSuggestions > 0 && assessment.decision == null && !isScoringRun
  const projected = showProjection ? projectedScore(assessment.criteria) : null

  // Category rows: template order first, then orphan categories (criteria
  // whose category was removed from the template), humanized.
  const byCategory = new Map<string, GoNoGoAssessment["criteria"]>()
  for (const c of assessment.criteria) {
    byCategory.set(c.category, [...(byCategory.get(c.category) ?? []), c])
  }
  const knownSlugs = new Set(categoryDefs.map((d) => d.slug))
  const rows = [
    ...categoryDefs
      .filter((d) => byCategory.has(d.slug))
      .map((d) => ({ slug: d.slug, label: d.label })),
    ...[...byCategory.keys()]
      .filter((slug) => !knownSlugs.has(slug))
      .map((slug) => ({ slug, label: humanizeCategorySlug(slug) })),
  ]

  return (
    <div className="border border-border rounded-xl bg-card shadow-sm p-5 flex flex-col gap-6 sm:flex-row">
      {/* Verdict */}
      <div className="flex-[1.2] min-w-0">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">Overall Score</span>
          <span className="text-meta">{scored} of {total} criteria scored</span>
        </div>
        <div className="mt-3 flex items-baseline gap-1.5">
          <span className="text-4xl font-semibold leading-none tracking-tight tabular-nums">{score}</span>
          <span className="text-sm text-muted-foreground">/ 100</span>
          {rec ? (
            <Badge className={cn("ml-3 rounded-full border-transparent", TONE_PILL_CLASS[tone])} dot>
              {RECOMMENDATION_LABEL[rec]}
            </Badge>
          ) : !showProjection ? (
            <span className="ml-3 text-meta">Score more criteria</span>
          ) : null}
          {projected != null && (
            <span className="ml-3 inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--brand)/0.08)] px-2.5 py-0.5 text-[11px] font-medium text-[hsl(var(--ai-accent-text))]">
              <GradientSparkleIcon className="h-3 w-3" />
              AI suggests {projected}
            </span>
          )}
        </div>
        {/* Zone track: faint zone tints, threshold ticks, fill colored by zone */}
        <div className="mt-4">
          <div className="relative h-2 rounded-full overflow-hidden">
            <div className="absolute inset-y-0 left-0 bg-[hsl(var(--destructive)/0.14)]" style={{ width: `${conditionalGo}%` }} />
            <div className="absolute inset-y-0 bg-[hsl(var(--status-warning)/0.16)]" style={{ left: `${conditionalGo}%`, width: `${go - conditionalGo}%` }} />
            <div className="absolute inset-y-0 bg-[hsl(var(--status-success)/0.14)]" style={{ left: `${go}%`, width: `${100 - go}%` }} />
            {projected != null && (
              <div
                className="absolute inset-y-0 left-0 rounded-full bg-[hsl(var(--brand)/0.28)] transition-all duration-300"
                style={{ width: `${Math.min(projected, 100)}%` }}
              />
            )}
            {hasScores && (
              <div
                className={cn("absolute inset-y-0 left-0 rounded-full transition-all duration-300", TONE_BAR_CLASS[tone])}
                style={{ width: `${Math.min(score, 100)}%` }}
              />
            )}
          </div>
          <div className="relative mt-1 h-4 text-[10.5px] text-muted-foreground">
            <span className="absolute left-0">No go</span>
            <span className="absolute -translate-x-1/2 tabular-nums" style={{ left: `${conditionalGo}%` }}>{conditionalGo}</span>
            <span className="absolute -translate-x-1/2 tabular-nums" style={{ left: `${go}%` }}>{go}</span>
            <span className="absolute right-0">Go</span>
          </div>
        </div>
      </div>

      {/* Shape of the deal: per-category mini-bars */}
      {rows.length > 0 && (
        <div className="flex-1 min-w-0 flex flex-col justify-center gap-2 sm:border-l sm:border-border sm:pl-6">
          {rows.map((row) => {
            const avg = categoryAverage(byCategory.get(row.slug) ?? [])
            return (
              <div key={row.slug} className="grid grid-cols-[100px_1fr_32px] items-center gap-2 text-xs">
                <span className="text-muted-foreground truncate">{displayCategoryLabel(row.label)}</span>
                <div className="h-[5px] rounded-full bg-muted overflow-hidden">
                  {avg != null && (
                    <div
                      className={cn("h-full rounded-full", TONE_BAR_CLASS[categoryAvgTone(avg)])}
                      style={{ width: `${(avg / 5) * 100}%` }}
                    />
                  )}
                </div>
                <span className={cn("text-right tabular-nums", avg != null ? "font-medium" : "text-muted-foreground")}>
                  {avg != null ? avg.toFixed(1) : "—"}
                </span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
